import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { fetchPage } from 'actions/pages';
import { createEditorSession, otpResend, submitContactForm, submitOtpForm } from 'actions/editor';
import { fetchMappingsForContentGrid, updateFiltersInContenGrid, resetFiltersInContenGrid } from 'actions/contentGrid';
import { fetchPreviewForAsset } from 'actions/content';
import { fetchCustomFields } from 'actions/fields/fields';

import { LIVE_ELEMENT, PAGE } from 'constants/widgetTypes';

import _ from 'lodash';
import cx from 'classnames';
import shortid from 'shortid';

import { Route, Switch, useRouteMatch, useParams, useHistory } from 'react-router-dom';

import Device from 'components/Device';
import {
  InteractiveContent, LandingPage, Widgets,
  BrandThemeManager,
  Fonts, GatingManager,
  WidgetsContext,
  FormContext,
  WidgetContent,
  WidgetActions
} from '@paperflite-marketing/cleverstory-commons';

import { registerEditorSessionReducer, useEditorSession } from 'components/Editor/hooks/useEditorSession';
import { useMediaQuery } from 'react-responsive';

import '@paperflite-marketing/asset-viewer/dist/index.css';
// import 'video.js/dist/video-js.min.css';
import styles from './versionPreview.module.scss';
import { contentGrid } from 'components/Palette/contentGrid';
import { VersionHistory } from './versionHistory';
import { fetchVersion } from 'actions/versions';
import { Loader } from 'components/ApplicationLoader';

const production = process.env.REACT_APP_DEVELOPMENT === 'false';
const IMAGE_CDN_ENDPOINT = process.env.REACT_APP_IMAGE_CDN;

export function VersionPreview() {
  let { path, url } = useRouteMatch();
  let { id } = useParams();
  const iframe = document.getElementById("cs-version-preview-iframe");

  const restoring = useSelector(state=> _.get(state, 'versions.restoring', false));
  const page = useSelector((state) => _.get(state, `pages.page.${id}.item`));
  const storyboard = _.get(page, 'storyboard', null);
  
  const [device, setDevice] = useState('none');

  const handleDeviceLayout = (type) => {
    setDevice(type);
  }

  if(restoring){
    return(
      <Loader title={"Restoring Version..." }/>
    )
  }

  return (
    <div className={styles.preview}>
      <Device className={cx(styles.devices, styles[device])} mode={device}>
        <iframe key={device} id='cs-version-preview-iframe' src={`${url}/embedded`}></iframe>
      </Device>

      <VersionHistory device={device} handleDeviceLayout={handleDeviceLayout} />
    </div>
  );
}

export function EmbeddedVersionPreview(props) {
  const dispatch = useDispatch();
  const history = useHistory();

  const editorRef = React.useRef(`editors_${shortid()}`);
  const editorSession = useEditorSession(editorRef.current);
  const gatingRef = React.useRef(null);

  const { id, versionId } = useParams();

  const [widgetContextState, setWidgetContextState] = useState({
    selector: ''
  });
  const [selectedDomain,setSelectedDomain] =useState({})

  const [formContextState, setFormContextState] = useState({
    onFormSubmit: () => { },
    onOtpFormSubmit: () => { },
    onOtpResend: () => { }
  });

  const [windowHeight, setWindowHeight] = useState('100vh');

  const page = useSelector((state) => _.get(state, `pages.page.${id}.item`));
  const pageLoaded = _.get(page, "id") != null;

  const storyboard = _.get(page, 'storyboard', null);
  const storyboardType = _.get(page, 'storyboard.type', '');
  const appearance = _.get(storyboard, 'appearance', {}) || {}
  const storyboardId = useSelector(state => _.get(state, 'storyboards.storyboard.item.id', null));

  const gatingConfigurations = useSelector(state => _.get(state, `pages.page.${id}.item.gatings`, []));
  var domain = useSelector(state => _.get(state, 'workspaces.workspace.item.defaultDomain') || "");

  const versionWidgets = useSelector(state => _.get(state, 'versions.preview', []));
  const versionLoading = useSelector(state=> _.get(state, 'versions.loading', false));
  const versionError = useSelector(state=> _.get(state, 'versions.error', false));
  const pageError = useSelector(state=> _.get(state, `pages.page.${id}.error`, null));

  const pageWidgets = useSelector((state) => {
    const widgets = _.get(state, `${widgetContextState.selector}.widgets`) || [];
    var pageSections = widgets.filter(item => {
      return item.type === PAGE;
    });

    if (storyboardType === 'INTERACTIVE_CONTENT'){
      pageSections = pageSections.filter((item) => _.get(item, "properties.desktop.visible") === true );
    }

    pageSections = _.sortBy(pageSections, 'order');

    return pageSections;
  });

  const fonts = useSelector(state => _.get(state, `pages.page.${id}.item.draft.fonts`) || []);
  Fonts.useFont(fonts);

  const desktop = useMediaQuery({
    query: '(min-width: 1024px)'
  });

  useEffect(() => {
    if(versionId == "current"){
      dispatch(fetchPage(id));
      return
    }
    
    let request = {
      "storyboard": storyboardId,
      "page": id,
      "snapshot": versionId
    }
    dispatch(fetchPage(id));
    dispatch(fetchVersion(request));
  }, [id, versionId]);

  useEffect(() => {
    if (editorRef.current && pageLoaded) {
      const widgets = (versionId == "current") ? _.get(page, "draft.widgets") : versionWidgets;

      var activePage = null;
      var pageSections = widgets.filter(item => {
        return item.type === PAGE;
      });

      pageSections = _.sortBy(pageSections, 'order');

      if (_.size(pageSections) > 0) {
        activePage = pageSections[0].uid;
      }

      const initialState = {
        widgets: widgets,
        interactive: {
          active: activePage
        }
      }

      dispatch(createEditorSession(editorRef.current, initialState));

      setWidgetContextState({
        session: editorRef.current,
        selector: `${editorRef.current}.present`,
        breakpoint: desktop ? 'desktop' : 'mobile',
        imageCDN: IMAGE_CDN_ENDPOINT,
        shouldZoom: true,
        development: false,
        domain: _.get(selectedDomain, 'url'),
        createLiveElementContext: (element) => {
          const sessionId = `editors_live_element_${element.uid}`;

          return {
            ...widgetContextState,
            session: sessionId,
            selector: `${sessionId}.present`
          }
        },
        updateCarouselSlide: (payload) => {
          setWidgetContextState(prev => ({
            ...prev,
            carouselSlide: payload
          }));
        }
      });

      setFormContextState({
        onFormSubmit: handleFormSubmit,
        onOtpFormSubmit: handleOtpFormSubmit,
        onOtpResend: handleOtpResend
      });

      widgets.filter(item => item.type === LIVE_ELEMENT).forEach(item => {
        const sessionId = `editors_live_element_${item.uid}`;
        registerEditorSessionReducer(sessionId);
        dispatch(createEditorSession(sessionId, {
          widgets: item.children
        }));
      });
    }
  }, [editorRef.current, pageLoaded]);

  useEffect(() => {
    setWidgetContextState((state) => {
      return {
        ...state,
        breakpoint: desktop ? 'desktop' : 'mobile'
      }
    });
  }, [desktop]);

  useEffect(() => {
    window.addEventListener('resize', handleWindowResize);
    handleWindowResize();

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    }
  }, []);

  useEffect(() => {
    if (storyboard) {
      setWidgetContextState((state) => {
        return {
          ...state,
          storyboard: storyboard
        }
      });

      dispatch(fetchCustomFields(_.get(storyboard, 'id')))
    }

    let domainUrl = _.get(storyboard,'domain') || domain;
    setSelectedDomain(domainUrl);
  }, [storyboard]);

  const handleChange = (type, data) => {
    if (type === 'index') {
      //console.log("Current page -" + data.index);
    } else if (type === 'progress') {
      //console.log(`Page ${data.index} - progress ${data.progress}`);
    }
  }

  const handleFormSubmit = (editorSession, formId, fields) => {
    dispatch(submitContactForm(editorSession, formId));
  }

  const handleOtpFormSubmit = (editorSession, formId, fields) => {
    dispatch(submitOtpForm(editorSession, formId));
  }

  const handleOtpResend = (editorSession, formId, fields) => {
    dispatch(otpResend(editorSession, formId));
  }

  const handleWindowResize = () => {
    const height = window.innerHeight;
    setWindowHeight(`${height}px`);
  }

  if(versionLoading){
    return(
      <Loader title={"Loading Version Preview..." }/>
    )
  }

  if(pageError || (!versionLoading && versionError)){
    return(
      <div className={styles.errorMessageContainer}>
        <h2 className={styles.errorMessageTitle}>Page not part of this version</h2>
        <p className={styles.errorMessageSubText}>This page has been removed within the selected version history.</p>
      </div>
    )
  }

  return (
    <div key={widgetContextState.breakpoint} className={styles.content}>
      <style>
        {`
          :root{
            --window-height: ${windowHeight};
          }
        `}
      </style>

      <BrandThemeManager className={".cs_text_widget"} themes={_.get(storyboard, 'themes')} />

      <WidgetsContext.Provider value={widgetContextState}>
        <FormContext.Provider value={formContextState}>
          <GatingManager ref={gatingRef}
            configurations={gatingConfigurations}
            page={id}
            breakpoint={widgetContextState.breakpoint}
            preview>
            <WidgetContent.Manager
              debug={!production}
              fetchMappingsForContentGrid={(contentGrid, page, limit) => {
                dispatch(fetchMappingsForContentGrid(editorSession.id, contentGrid, page, limit, true));
              }}
              fetchPreviewDataForAsset={(asset) => {
                dispatch(fetchPreviewForAsset(asset));
              }}
              updateFiltersInContenGrid={(contentGrid, filters) => {
                dispatch(updateFiltersInContenGrid(editorSession.id, contentGrid, filters));
              }}
              resetFiltersInContenGrid={(contentGrid) => {
                dispatch(resetFiltersInContenGrid(editorSession.id, contentGrid));
              }}
              onContentChange={(content) => {
                const url = _.get(content, 'content.alias');
                const name = _.get(content, 'content.name');
                window.history.replaceState(null, name, url);
              }}
              onAnalyticsChange={(asset, analytics) => {

              }}
              onDownload={(asset) => {
                console.log(`Download Asset ${asset.id}`);
              }}
              onClose={() => {
                console.log("Close Dialog");
              }}
            >
              <WidgetActions.Provider value={{
                onBeforeExecution: () => { }
              }}>
                {storyboardType === 'INTERACTIVE_CONTENT' &&
                  <InteractiveContent initialPage={0} appearance={appearance} onChange={handleChange}>
                    {pageWidgets.map((widget, index) => {
                      return <Widgets key={widget.uid} widgetProps={widget} breakpoint={widgetContextState.breakpoint} />
                    })}
                  </InteractiveContent>
                }

                {storyboardType !== 'INTERACTIVE_CONTENT' &&
                  <LandingPage onChange={handleChange} pageWidgets={pageWidgets}>
                    {pageWidgets.map((widget, index) => {
                      return <Widgets key={widget.uid} widgetProps={widget} breakpoint={widgetContextState.breakpoint} />
                    })}
                  </LandingPage>
                }
              </WidgetActions.Provider>
            </WidgetContent.Manager>
          </GatingManager >
        </FormContext.Provider>
      </WidgetsContext.Provider>
    </div >
  );
}